// src/features/academic/pages/LeaderboardPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Trophy, Medal, Search, FilterIcon, Calendar, School } from 'lucide-react';
import { useAuth } from '../../../core/contexts/AuthContext';
import Leaderboard from '../components/Leaderboard';
import UserScore from '../components/UserScore';
import UniversityRankingItem from '../components/UniversityRankingItem';
import challengeService from '../services/challengeService';

/**
 * LeaderboardPage Component
 * Dedicated page to view and filter the academic challenge leaderboard
 */
const LeaderboardPage = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [leaderboard, setLeaderboard] = useState([]);
  const [universityRankings, setUniversityRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingUniversities, setLoadingUniversities] = useState(true);
  const [userRank, setUserRank] = useState(null);
  const [totalScore, setTotalScore] = useState(0);
  const [activeTab, setActiveTab] = useState('individual'); // 'individual' or 'university'
  const [filterText, setFilterText] = useState('');
  const [timeFilter, setTimeFilter] = useState('all'); // 'all', 'week', 'month', 'year'
  
  // Fetch leaderboard data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Set both loading states
        setLoading(true);
        setLoadingUniversities(true);
        
        // Fetch individual leaderboard
        const individualData = await challengeService.fetchLeaderboard(30);
        setLeaderboard(individualData);
        
        // Find user's rank
        if (currentUser) {
          const userEntry = individualData.find(entry => entry.userId === currentUser.uid);
          if (userEntry) {
            setUserRank(userEntry.rank);
          } else {
            // If not in top 30, get actual rank
            const userRankPosition = await challengeService.getUserRankingPosition(currentUser.uid);
            if (userRankPosition) {
              setUserRank(userRankPosition);
            }
          }
        }
        
        // Fetch university leaderboard
        const universityData = await challengeService.fetchUniversityRankings(20);
        setUniversityRankings(universityData);
        
        // Fetch user's total score
        if (currentUser) {
          const userScoreData = await challengeService.fetchUserScores(currentUser.uid);
          if (userScoreData) {
            setTotalScore(userScoreData.totalScore || 0);
          }
        }
      } catch (error) {
        console.error('Error fetching leaderboards:', error);
      } finally {
        setLoading(false);
        setLoadingUniversities(false);
      }
    };
    
    fetchData();
  }, [currentUser]);
  
  // Filter leaderboard based on search text
  const getFilteredLeaderboard = () => {
    if (!filterText.trim()) {
      return activeTab === 'individual' ? leaderboard : universityRankings;
    }
    
    const searchTerm = filterText.toLowerCase();
    
    if (activeTab === 'individual') {
      return leaderboard.filter(entry => 
        (entry.name && entry.name.toLowerCase().includes(searchTerm)) ||
        (entry.university && entry.university.toLowerCase().includes(searchTerm))
      );
    } else {
      return universityRankings.filter(entry =>
        entry.name.toLowerCase().includes(searchTerm)
      );
    }
  };
  
  // Navigation handler
  const handleBack = () => {
    navigate('/academic/challenge');
  };
  
  // Get user's university
  const userUniversity = userProfile?.academicInfo?.university || null;
  
  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="max-w-5xl mx-auto px-4 py-6 pt-16">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={handleBack}
            className="text-gray-600 hover:text-gray-800 flex items-center"
            aria-label="Back to Academic Challenge"
          >
            <ArrowLeft size={20} className="mr-1" />
            <span>Back</span>
          </button>
          
          <h1 className="text-2xl font-bold text-center text-gray-800 flex-1 flex items-center justify-center">
            <Trophy className="mr-2 text-amber-500" size={24} />
            Challenge Leaderboard
          </h1>
          
          <div className="w-20"></div> {/* Empty space for balance */}
        </div>
        
        {/* User Score */}
        {currentUser && (
          <div className="mb-6">
            <UserScore
              todayScore={0} // This would come from today's challenge
              totalScore={totalScore}
              rank={userRank}
              hasCompletedToday={false} // Would need to check this
              userName={userProfile?.basicInfo?.name}
              userAvatar={userProfile?.photos?.[0]}
              university={userUniversity}
            />
          </div>
        )}
        
        {/* Leaderboard Controls */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          {/* Tabs */}
          <div className="flex border-b mb-4">
            <button
              className={`flex-1 py-2 text-center font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'individual'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('individual')}
            >
              <span className="flex items-center justify-center">
                <Trophy size={16} className="mr-1" />
                Individual Rankings
              </span>
            </button>
            <button
              className={`flex-1 py-2 text-center font-medium text-sm border-b-2 transition-colors ${
                activeTab === 'university'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
              onClick={() => setActiveTab('university')}
            >
              <span className="flex items-center justify-center">
                <School size={16} className="mr-1" />
                University Rankings
              </span>
            </button>
          </div>
          
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={16} className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder={`Search ${activeTab === 'individual' ? 'users' : 'universities'}...`}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
            </div>
            
            <div className="relative">
              <select 
                className="appearance-none pl-3 pr-10 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
                aria-label="Time filter"
              >
                <option value="all">All Time</option>
                <option value="year">This Year</option>
                <option value="month">This Month</option>
                <option value="week">This Week</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <Calendar size={14} className="text-gray-400" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Leaderboard */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {activeTab === 'individual' ? (
            // Individual Rankings
            <>
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin w-10 h-10 border-2 border-green-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading leaderboard...</p>
                </div>
              ) : getFilteredLeaderboard().length === 0 ? (
                <div className="p-8 text-center">
                  <Trophy className="mx-auto text-gray-300 mb-3" size={32} />
                  <h3 className="text-gray-700 font-medium">No Results</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    No users found matching your criteria.
                  </p>
                </div>
              ) : (
                <Leaderboard
                  entries={getFilteredLeaderboard()}
                  loading={false}
                  currentUserId={currentUser?.uid}
                  title="Individual Rankings"
                />
              )}
            </>
          ) : (
            // University Rankings
            <>
              <div className="bg-gradient-to-r from-green-500 to-teal-500 text-white p-4">
                <h2 className="font-bold text-lg flex items-center">
                  <School className="mr-2" size={22} />
                  University Challenge Rankings
                </h2>
              </div>
              
              <div className="p-4">
                {loadingUniversities ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin w-10 h-10 border-2 border-green-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading university rankings...</p>
                  </div>
                ) : getFilteredLeaderboard().length === 0 ? (
                  <div className="p-8 text-center">
                    <School className="mx-auto text-gray-300 mb-3" size={32} />
                    <h3 className="text-gray-700 font-medium">No Results</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      No universities found matching your criteria.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Top 3 Podium */}
                    {getFilteredLeaderboard().length > 2 && (
                      <div className="flex items-end justify-center mb-8 mt-4">
                        {/* Second Place */}
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2 }}
                          className="w-24 mx-2"
                        >
                          <div className="text-center mb-2">
                            <div className="font-medium text-sm text-gray-800 truncate">
                              {getFilteredLeaderboard()[1].name}
                            </div>
                            <div className="text-xs text-gray-600">
                              {getFilteredLeaderboard()[1].totalScore.toLocaleString()} pts
                            </div>
                          </div>
                          <div className="h-16 bg-gray-200 rounded-t-lg relative flex items-end justify-center">
                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                              <Medal size={24} className="text-gray-400" />
                            </div>
                            <div className="text-gray-700 font-bold pb-1">2</div>
                          </div>
                        </motion.div>
                        
                        {/* First Place */}
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 }}
                          className="w-28 mx-2 z-10"
                        >
                          <div className="text-center mb-2">
                            <div className="font-medium text-base text-gray-800 truncate">
                              {getFilteredLeaderboard()[0].name}
                            </div>
                            <div className="text-sm text-yellow-600 font-bold">
                              {getFilteredLeaderboard()[0].totalScore.toLocaleString()} pts
                            </div>
                          </div>
                          <div className="h-24 bg-yellow-400 rounded-t-lg relative flex items-end justify-center">
                            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                              <Trophy size={28} className="text-yellow-600" />
                            </div>
                            <div className="text-yellow-800 font-bold pb-1">1</div>
                          </div>
                        </motion.div>
                        
                        {/* Third Place */}
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                          className="w-24 mx-2"
                        >
                          <div className="text-center mb-2">
                            <div className="font-medium text-sm text-gray-800 truncate">
                              {getFilteredLeaderboard()[2].name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {getFilteredLeaderboard()[2].totalScore.toLocaleString()} pts
                            </div>
                          </div>
                          <div className="h-12 bg-amber-700 rounded-t-lg relative flex items-end justify-center">
                            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                              <Medal size={24} className="text-amber-600" />
                            </div>
                            <div className="text-amber-100 font-bold pb-1">3</div>
                          </div>
                        </motion.div>
                      </div>
                    )}
                    
                    {/* Remaining Universities */}
                    <div className="mt-6 space-y-3">
                      {getFilteredLeaderboard().slice(3).map((entry) => (
                        <UniversityRankingItem
                          key={entry.id || entry.name}
                          rank={entry.rank}
                          name={entry.name}
                          score={entry.totalScore}
                          participants={entry.participants}
                          isHighlighted={entry.name === userUniversity}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
        
        {/* Legend/Info Section */}
        <div className="mt-6 bg-blue-50 p-4 rounded-lg border border-blue-100">
          <h3 className="text-sm font-medium text-blue-800 mb-2 flex items-center">
            <Trophy size={16} className="mr-2 text-blue-700" />
            How Points Are Calculated
          </h3>
          <p className="text-sm text-blue-700">
            Students earn 10 points for each correct answer in the daily academic challenge. 
            Your scores contribute to your individual ranking as well as your university's position.
          </p>
          
          {activeTab === 'university' && (
            <p className="text-sm text-blue-700 mt-2">
              University rankings are based on the combined scores of all participants from that institution.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPage;